import {
    getAuthData,
    getRefreshToken,
    getToken,
    removeToken,
    saveAuthData,
    saveRefreshToken,
    saveToken,
} from "@/shared/lib/token";

/**
 * Sessão de suporte da equipa 7Days ("a ver como <pessoa>").
 *
 * O token de suporte dura 15 minutos e não tem refresh. Enquanto ele vale, a sessão do admin
 * fica guardada aqui, no sessionStorage da aba, para ser reposta ao fim.
 */
const SUPPORT_SESSION_KEY = "@supportSession";

export interface SupportSession {
    /** Quem está a ser visto; só para a faixa. */
    targetLabel: string;
    /** Instante (ms) em que o token de suporte expira. */
    endsAt: number;
    /** Para onde voltar ao terminar. */
    returnUrl: string;
    /**
     * Sessão a repor no fim. Nula quando a sessão veio do console (outra origem): a aba do app
     * não tinha sessão própria, então terminar só limpa o token e volta ao console.
     */
    admin: {
        accessToken: string;
        refreshToken: string | null;
        permissions: string[];
        roles: string[];
    } | null;
}

const isBrowser = () => typeof window !== "undefined";

function isSupportSession(value: unknown): value is SupportSession {
    if (!value || typeof value !== "object") return false;
    const candidate = value as Partial<SupportSession>;
    return (
        typeof candidate.targetLabel === "string" &&
        typeof candidate.endsAt === "number" &&
        typeof candidate.returnUrl === "string" &&
        (candidate.admin === null || typeof candidate.admin?.accessToken === "string")
    );
}

export function getSupportSession(): SupportSession | null {
    if (!isBrowser()) return null;

    const raw = sessionStorage.getItem(SUPPORT_SESSION_KEY);
    if (!raw) return null;

    try {
        const parsed: unknown = JSON.parse(raw);
        if (isSupportSession(parsed)) return parsed;
    } catch {
        /* conteúdo corrompido: descarta abaixo */
    }
    sessionStorage.removeItem(SUPPORT_SESSION_KEY);
    return null;
}

interface StartSupportSessionInput {
    accessToken: string;
    expiresInSeconds: number;
    targetLabel: string;
    returnUrl: string;
}

function storeSupportSession(input: StartSupportSessionInput, admin: SupportSession["admin"]): void {
    const session: SupportSession = {
        targetLabel: input.targetLabel,
        endsAt: Date.now() + input.expiresInSeconds * 1000,
        returnUrl: input.returnUrl,
        admin,
    };
    sessionStorage.setItem(SUPPORT_SESSION_KEY, JSON.stringify(session));

    removeToken();
    saveToken(input.accessToken);
}

/** Guarda a sessão do admin e passa a usar o token de suporte. Sem refresh: ele não existe. */
export function beginSupportSession(input: StartSupportSessionInput): void {
    const adminToken = getToken();
    if (!isBrowser() || !adminToken) {
        throw new Error("Sem sessão de admin para guardar");
    }

    const { permissions, roles } = getAuthData();
    storeSupportSession(input, { accessToken: adminToken, refreshToken: getRefreshToken(), permissions, roles });
}

/**
 * Sessão de suporte recebida do console. A sessão do app que já existir nesta origem é
 * guardada e reposta no fim, como numa sessão aberta no próprio app.
 */
export function beginHandedOffSupportSession(input: StartSupportSessionInput): void {
    if (!isBrowser()) throw new Error("Sessão de suporte só existe no browser");
    const adminToken = getToken();
    const { permissions, roles } = getAuthData();
    storeSupportSession(input, adminToken ? { accessToken: adminToken, refreshToken: getRefreshToken(), permissions, roles } : null);
}

/** Repõe a sessão do admin. Devolve para onde navegar, ou null se não havia sessão de suporte. */
export function endSupportSession(): string | null {
    const session = getSupportSession();
    if (!session) return null;

    sessionStorage.removeItem(SUPPORT_SESSION_KEY);
    removeToken();
    if (!session.admin) {
        saveAuthData([], []);
        return session.returnUrl;
    }
    saveToken(session.admin.accessToken);
    if (session.admin.refreshToken) {
        saveRefreshToken(session.admin.refreshToken);
    }
    saveAuthData(session.admin.permissions, session.admin.roles);
    return session.returnUrl;
}

const READ_ONLY_PREFIXES = ["CAN_GET_", "CAN_LIST_", "CAN_VIEW_", "CAN_ACCESS_"] as const;

/** Na sessão de suporte o backend recusa qualquer escrita; a UI esconde o que não vai funcionar. */
export function isReadOnlyPermission(permission: string): boolean {
    return READ_ONLY_PREFIXES.some((prefix) => permission.startsWith(prefix));
}
